import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { PageHeader, SectionDivider } from "@/components/primitives";
import { getPlant, updatePlant, deletePlant } from "@/lib/api";
import { fileToSquareJpeg } from "@/lib/image";
import { toast } from "sonner";
import { Star, Trash2, ArrowLeft, Sprout, Pencil, Save, X, ImagePlus } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const STAGES = ["purchased", "planted", "germinated", "flowering", "dormant", "finished"];
const CATEGORIES = ["dahlia", "rose", "tulip", "vegetable", "herb", "other"];
const TYPES = ["seed", "tuber", "cutting", "bulb", "plant"];

export default function PlantDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [plant, setPlant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    (async () => {
      try { setPlant(await getPlant(id)); }
      catch { toast.error("Plant not found"); nav("/garden"); }
      finally { setLoading(false); }
    })();
  }, [id, nav]);

  const beginEdit = () => {
    setForm({
      name: plant.name || "", category: plant.category || "dahlia",
      type: plant.type || "tuber", variety: plant.variety || "",
      color: plant.color || "", vendor: plant.vendor || "",
      purchase_date: plant.purchase_date || "", cost: plant.cost ?? "",
      quantity: plant.quantity ?? 1,
      stage: plant.stage || "purchased",
      planted_date: plant.planted_date || "",
      germinated_date: plant.germinated_date || "",
      first_bloom_date: plant.first_bloom_date || "",
      position: plant.position || "",
      notes: plant.notes || "",
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        cost: form.cost === "" ? null : Number(form.cost),
        quantity: form.quantity === "" || form.quantity == null ? 1 : Math.max(1, Math.floor(Number(form.quantity))),
        purchase_date: form.purchase_date || null,
        planted_date: form.planted_date || null,
        germinated_date: form.germinated_date || null,
        first_bloom_date: form.first_bloom_date || null,
      };
      const updated = await updatePlant(id, payload);
      setPlant(updated); setEditing(false);
      toast.success("Saved");
    } catch { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  const setStage = async (s) => {
    try { const updated = await updatePlant(id, { stage: s }); setPlant(updated); toast.success(`Stage updated to ${s}`); }
    catch { toast.error("Could not update"); }
  };
  const toggleFav = async () => {
    try { const updated = await updatePlant(id, { favorite: !plant.favorite }); setPlant(updated); }
    catch { toast.error("Could not update"); }
  };
  const remove = async () => {
    try { await deletePlant(id); toast.success("Specimen removed"); nav("/garden"); }
    catch { toast.error("Could not delete"); }
  };

  const addPhoto = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    setUploading(true);
    try {
      const data = await fileToSquareJpeg(f, 800, 0.85);
      const updated = await updatePlant(id, { photos: [...(plant.photos || []), data] });
      setPlant(updated); toast.success("Photo added");
    } catch (err) { toast.error(err.message || "Could not add photo"); }
    finally { setUploading(false); }
  };

  const removePhoto = async (idx) => {
    const next = (plant.photos || []).filter((_, i) => i !== idx);
    try { const updated = await updatePlant(id, { photos: next }); setPlant(updated); }
    catch { toast.error("Could not remove"); }
  };

  if (loading) return <div className="text-center mt-20 font-display italic text-ink-soft" data-testid="plant-loading">Gathering...</div>;
  if (!plant) return null;

  return (
    <div data-testid={`plant-detail-${plant.id}`}>
      <div className="pt-6">
        <Link to="/garden" className="chip" data-testid="plant-back">
          <ArrowLeft size={14} /> Back to garden
        </Link>
      </div>

      <PageHeader kicker="Specimen" title={plant.name} subtitle={plant.variety || "A specimen in the patch"} />

      <div className="flex flex-wrap items-center gap-2 justify-center -mt-2 mb-6">
        {!editing && (
          <>
            <button onClick={toggleFav} className="chip" data-active={plant.favorite} data-testid="plant-fav-toggle">
              <Star size={14} fill={plant.favorite ? "currentColor" : "none"} />
              {plant.favorite ? "Favourite" : "Add to favourites"}
            </button>
            <button onClick={beginEdit} className="chip" data-testid="plant-edit-toggle">
              <Pencil size={14} /> Edit
            </button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button className="chip" data-testid="plant-delete-trigger">
                  <Trash2 size={14} className="text-terracotta" /> Remove
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-cream-soft border-heritage-border">
                <AlertDialogHeader>
                  <AlertDialogTitle className="font-display text-2xl text-sage-dark">Remove this specimen?</AlertDialogTitle>
                  <AlertDialogDescription className="text-ink-soft">
                    This will lift {plant.name} from your patch permanently.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="btn-ghost" data-testid="plant-delete-cancel">Keep it</AlertDialogCancel>
                  <AlertDialogAction onClick={remove} className="btn-heritage" data-testid="plant-delete-confirm">
                    Lift the tuber
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </>
        )}
        {editing && (
          <>
            <button onClick={() => setEditing(false)} className="chip" data-testid="plant-edit-cancel">
              <X size={14} /> Cancel
            </button>
            <button onClick={saveEdit} disabled={saving} className="btn-heritage text-sm py-2 px-4" data-testid="plant-edit-save">
              <Save size={14} /> {saving ? "Saving..." : "Save changes"}
            </button>
          </>
        )}
      </div>

      <div className="h-card p-6 shadow-card">
        {!editing ? (
          <ReadOnlyView plant={plant} onStage={setStage} />
        ) : (
          <EditForm form={form} setForm={setForm} />
        )}

        {/* Photos */}
        <SectionDivider label="Photographs" className="mt-8" />
        <div className="mt-4 grid grid-cols-3 gap-2" data-testid="plant-photos-grid">
          {(plant.photos || []).map((src, i) => (
            <div key={i} className="relative aspect-square rounded-lg overflow-hidden bg-cream-deep" data-testid={`plant-photo-${i}`}>
              <img src={src} alt={`${plant.name} ${i + 1}`} className="w-full h-full object-cover" />
              <button
                onClick={() => removePhoto(i)}
                className="absolute top-1 right-1 w-7 h-7 rounded-full bg-cream-soft/95 border border-heritage-border text-terracotta flex items-center justify-center"
                aria-label="Remove photo"
                data-testid={`plant-photo-remove-${i}`}
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}
          <label className="aspect-square rounded-lg border-2 border-dashed border-heritage-border flex flex-col items-center justify-center text-sage-dark cursor-pointer hover:bg-cream-deep/40 transition-colors" data-testid="plant-photo-add">
            <ImagePlus size={22} />
            <span className="text-xs font-display italic mt-1">{uploading ? "Adding..." : "Add photo"}</span>
            <input type="file" accept="image/*" className="hidden" onChange={addPhoto} disabled={uploading} />
          </label>
        </div>
      </div>
    </div>
  );
}

function ReadOnlyView({ plant, onStage }) {
  return (
    <>
      <div className="flex items-center gap-4">
        <div className="w-16 h-16 rounded-xl flex items-center justify-center bg-cream-deep overflow-hidden">
          {plant.photos?.[0] ? <img src={plant.photos[0]} alt="" className="w-full h-full object-cover" /> : <Sprout size={28} className="text-sage" />}
        </div>
        <div>
          <div className="kicker">{plant.category} · {plant.type}</div>
          <div className="font-display text-2xl text-sage-dark">{plant.name}</div>
          {plant.color && <div className="text-ink-soft text-sm">{plant.color}</div>}
        </div>
      </div>

      <div className="mt-6">
        <div className="kicker mb-2">Stage</div>
        <div className="flex flex-wrap gap-2">
          {STAGES.map((s) => (
            <button key={s} className="chip" data-active={plant.stage === s} onClick={() => onStage(s)} data-testid={`plant-stage-${s}`}>
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-4">
        <Detail label="How many" value={plant.quantity && plant.quantity > 1 ? `${plant.quantity} of this variety` : "1"} />
        <Detail label="Vendor" value={plant.vendor} />
        <Detail label="Purchased" value={fmtDate(plant.purchase_date)} />
        <Detail label="Cost" value={plant.cost ? `NZ$ ${Number(plant.cost).toFixed(2)}` : "—"} />
        <Detail label="Position" value={plant.position} />
        <Detail label="Planted" value={fmtDate(plant.planted_date)} />
        <Detail label="Germinated" value={fmtDate(plant.germinated_date)} />
        <Detail label="First Bloom" value={fmtDate(plant.first_bloom_date)} />
      </div>

      {plant.notes && (
        <div className="mt-6">
          <div className="kicker mb-2">The Gardener's Notes</div>
          <p className="font-display italic text-lg text-ink-soft whitespace-pre-wrap leading-relaxed">{plant.notes}</p>
        </div>
      )}
    </>
  );
}

function EditForm({ form, setForm }) {
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <div className="space-y-4" data-testid="plant-edit-form">
      <label className="block">
        <span className="text-sm text-ink-soft mb-1.5 inline-block">Name *</span>
        <input className="field" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="plant-edit-name" />
      </label>
      <div>
        <p className="text-sm text-ink-soft mb-2">Category</p>
        <Chips options={CATEGORIES} value={form.category} onChange={(v) => set("category", v)} testid="plant-edit-category" />
      </div>
      <div>
        <p className="text-sm text-ink-soft mb-2">Type</p>
        <Chips options={TYPES} value={form.type} onChange={(v) => set("type", v)} testid="plant-edit-type" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Variety"><input className="field" value={form.variety} onChange={(e) => set("variety", e.target.value)} data-testid="plant-edit-variety" /></Field>
        <Field label="Color"><input className="field" value={form.color} onChange={(e) => set("color", e.target.value)} data-testid="plant-edit-color" /></Field>
      </div>
      <Field label="Vendor"><input className="field" value={form.vendor} onChange={(e) => set("vendor", e.target.value)} data-testid="plant-edit-vendor" /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Purchase Date"><input type="date" className="field" value={form.purchase_date} onChange={(e) => set("purchase_date", e.target.value)} /></Field>
        <Field label="Cost (NZ$)"><input type="number" step="0.01" className="field" value={form.cost} onChange={(e) => set("cost", e.target.value)} data-testid="plant-edit-cost" /></Field>
      </div>
      <Field label="How many of this variety">
        <input type="number" min="1" max="10000" step="1" className="field" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} data-testid="plant-edit-quantity" />
      </Field>
      <div>
        <p className="text-sm text-ink-soft mb-2">Stage</p>
        <Chips options={STAGES} value={form.stage} onChange={(v) => set("stage", v)} testid="plant-edit-stage" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Planted"><input type="date" className="field" value={form.planted_date} onChange={(e) => set("planted_date", e.target.value)} /></Field>
        <Field label="Germinated"><input type="date" className="field" value={form.germinated_date} onChange={(e) => set("germinated_date", e.target.value)} /></Field>
      </div>
      <Field label="First Bloom"><input type="date" className="field" value={form.first_bloom_date} onChange={(e) => set("first_bloom_date", e.target.value)} /></Field>
      <Field label="Position"><input className="field" value={form.position} onChange={(e) => set("position", e.target.value)} data-testid="plant-edit-position" /></Field>
      <Field label="Notes"><textarea rows={4} className="field resize-y" value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="plant-edit-notes" /></Field>
    </div>
  );
}

function Chips({ options, value, onChange, testid }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map((o) => (
        <button key={o} type="button" className="chip" data-active={value === o} onClick={() => onChange(o)}>
          {o.charAt(0).toUpperCase() + o.slice(1)}
        </button>
      ))}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-sm text-ink-soft mb-1.5 inline-block">{label}</span>
      {children}
    </label>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-ink-soft">{label}</div>
      <div className="font-display text-lg text-ink">{value || "—"}</div>
    </div>
  );
}

function fmtDate(s) {
  if (!s) return "";
  try { return new Date(s).toLocaleDateString("en-NZ", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return s; }
}
